/**
 * Offline Storage Service using IndexedDB
 * Stores questions, flashcards, and audios locally for offline use
 */

const DB_NAME = 'TuPlazaIA_Offline';
const DB_VERSION = 2;
const QUESTIONS_STORE = 'questions';
const FLASHCARDS_STORE = 'flashcards';
const AUDIOS_STORE = 'audios';
const RESULTS_STORE = 'pending_results';
const META_STORE = 'metadata';

class OfflineStorageService {
  constructor() {
    this.db = null;
    this.isReady = false;
  }

  async init() {
    if (this.isReady && this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Questions store
        if (!db.objectStoreNames.contains(QUESTIONS_STORE)) {
          const questionsStore = db.createObjectStore(QUESTIONS_STORE, { keyPath: 'question_id' });
          questionsStore.createIndex('topic_id', 'topic_id', { unique: false });
          questionsStore.createIndex('difficulty', 'difficulty', { unique: false });
        }

        // Flashcards store
        if (!db.objectStoreNames.contains(FLASHCARDS_STORE)) {
          const flashcardsStore = db.createObjectStore(FLASHCARDS_STORE, { keyPath: 'flashcard_id' });
          flashcardsStore.createIndex('topic_id', 'topic_id', { unique: false });
          flashcardsStore.createIndex('state', 'state', { unique: false });
        }

        // Audios store (metadata only)
        if (!db.objectStoreNames.contains(AUDIOS_STORE)) {
          const audiosStore = db.createObjectStore(AUDIOS_STORE, { keyPath: 'audio_id' });
          audiosStore.createIndex('topic_id', 'topic_id', { unique: false });
        }

        // Pending results store
        if (!db.objectStoreNames.contains(RESULTS_STORE)) {
          db.createObjectStore(RESULTS_STORE, { keyPath: 'offline_session_id' });
        }

        // Metadata store
        if (!db.objectStoreNames.contains(META_STORE)) {
          db.createObjectStore(META_STORE, { keyPath: 'key' });
        }
      };
    });
  }

  // ========== QUESTIONS ==========
  
  async saveQuestions(questions) {
    await this.init();
    const tx = this.db.transaction(QUESTIONS_STORE, 'readwrite');
    const store = tx.objectStore(QUESTIONS_STORE);

    for (const question of questions) {
      await new Promise((resolve, reject) => {
        const request = store.put(question);
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
    }

    await this.setMeta('lastDownload', new Date().toISOString());
    await this.setMeta('questionCount', questions.length);

    return questions.length;
  }

  async getQuestions(limit = null) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(QUESTIONS_STORE, 'readonly');
      const store = tx.objectStore(QUESTIONS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        let questions = request.result;
        if (limit) {
          questions = questions.sort(() => Math.random() - 0.5).slice(0, limit);
        }
        resolve(questions);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getQuestionsByTopic(topicId, limit = 10) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(QUESTIONS_STORE, 'readonly');
      const store = tx.objectStore(QUESTIONS_STORE);
      const index = store.index('topic_id');
      const request = index.getAll(topicId);

      request.onsuccess = () => {
        let questions = request.result;
        questions = questions.sort(() => Math.random() - 0.5).slice(0, limit);
        resolve(questions);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ========== FLASHCARDS ==========

  async saveFlashcards(flashcards) {
    await this.init();
    const tx = this.db.transaction(FLASHCARDS_STORE, 'readwrite');
    const store = tx.objectStore(FLASHCARDS_STORE);

    for (const card of flashcards) {
      // Ensure flashcard has an ID
      const cardWithId = {
        ...card,
        flashcard_id: card.flashcard_id || card.card_id || `fc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      await new Promise((resolve, reject) => {
        const request = store.put(cardWithId);
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
    }

    await this.setMeta('flashcardCount', flashcards.length);
    return flashcards.length;
  }

  async getFlashcards(limit = null) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(FLASHCARDS_STORE, 'readonly');
      const store = tx.objectStore(FLASHCARDS_STORE);
      const request = store.getAll();

      request.onsuccess = () => {
        let flashcards = request.result;
        if (limit) {
          flashcards = flashcards.sort(() => Math.random() - 0.5).slice(0, limit);
        }
        resolve(flashcards);
      };
      request.onerror = () => reject(request.error);
    });
  }

  // ========== AUDIOS ==========

  async saveAudios(audios) {
    await this.init();
    const tx = this.db.transaction(AUDIOS_STORE, 'readwrite');
    const store = tx.objectStore(AUDIOS_STORE);

    for (const audio of audios) {
      const audioWithId = {
        ...audio,
        audio_id: audio.audio_id || audio.id || `audio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      };
      await new Promise((resolve, reject) => {
        const request = store.put(audioWithId);
        request.onsuccess = resolve;
        request.onerror = () => reject(request.error);
      });
    }

    await this.setMeta('audioCount', audios.length);
    return audios.length;
  }

  async getAudios() {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(AUDIOS_STORE, 'readonly');
      const store = tx.objectStore(AUDIOS_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ========== RESULTS ==========

  async savePendingResult(result) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(RESULTS_STORE, 'readwrite');
      const store = tx.objectStore(RESULTS_STORE);
      const request = store.put(result);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingResults() {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(RESULTS_STORE, 'readonly');
      const store = tx.objectStore(RESULTS_STORE);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async clearPendingResults(sessionIds) {
    await this.init();
    const tx = this.db.transaction(RESULTS_STORE, 'readwrite');
    const store = tx.objectStore(RESULTS_STORE);

    for (const id of sessionIds) {
      await new Promise((resolve) => {
        const request = store.delete(id);
        request.onsuccess = resolve;
        request.onerror = resolve;
      });
    }
  }

  // ========== METADATA ==========

  async setMeta(key, value) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(META_STORE, 'readwrite');
      const store = tx.objectStore(META_STORE);
      const request = store.put({ key, value });

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getMeta(key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(META_STORE, 'readonly');
      const store = tx.objectStore(META_STORE);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result?.value);
      request.onerror = () => reject(request.error);
    });
  }

  // ========== STATS ==========

  async getStats() {
    await this.init();
    
    const questionCount = await this.getQuestionCount();
    const flashcardCount = await this.getFlashcardCount();
    const audioCount = await this.getAudioCount();
    const pendingResults = await this.getPendingResults();
    const lastDownload = await this.getMeta('lastDownload');

    return {
      questionCount,
      flashcardCount,
      audioCount,
      pendingResultsCount: pendingResults.length,
      lastDownload: lastDownload || null,
      isAvailable: questionCount > 0 || flashcardCount > 0
    };
  }

  async getQuestionCount() {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(QUESTIONS_STORE, 'readonly');
      const store = tx.objectStore(QUESTIONS_STORE);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getFlashcardCount() {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(FLASHCARDS_STORE, 'readonly');
      const store = tx.objectStore(FLASHCARDS_STORE);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAudioCount() {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(AUDIOS_STORE, 'readonly');
      const store = tx.objectStore(AUDIOS_STORE);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ========== CLEAR ==========

  async clearAll() {
    await this.init();
    const stores = [QUESTIONS_STORE, FLASHCARDS_STORE, AUDIOS_STORE, RESULTS_STORE, META_STORE];
    const tx = this.db.transaction(stores, 'readwrite');
    
    await Promise.all(stores.map(storeName => 
      new Promise((resolve) => {
        tx.objectStore(storeName).clear().onsuccess = resolve;
      })
    ));

    return true;
  }

  // ========== TOPICS ==========

  async getAvailableTopics() {
    await this.init();
    const questions = await this.getQuestions();
    const topicsMap = new Map();

    for (const q of questions) {
      if (q.topic_id && !topicsMap.has(q.topic_id)) {
        topicsMap.set(q.topic_id, {
          topic_id: q.topic_id,
          name: q.topic_name || 'Tema desconocido',
          question_count: 0
        });
      }
      if (q.topic_id) {
        topicsMap.get(q.topic_id).question_count++;
      }
    }

    return Array.from(topicsMap.values());
  }
}

// Singleton instance
export const offlineStorage = new OfflineStorageService();
export default offlineStorage;
